/**
 * BulkApproveModal — Phase R10.3 (2026-07-04)
 *
 * 4-state flow:
 *   1. loading-dry-run → POST /bulk-approve dry_run:true
 *   2. confirmation    → user reviews eligible / excluded counts
 *   3. executing       → POST /bulk-approve dry_run:false claim_ids:[]
 *   4. result          → shows approved + skipped breakdown
 *
 * Props:
 *   open               — bool, controls visibility
 *   onClose()          — called when user dismisses
 *   onCompleted()      — fired after a live approve finishes, parent should refetch
 *   branchFilter       — single branch_id to narrow dry-run scope, or null for all
 */
import { useEffect, useState } from 'react';
import { X, Loader2, Check, AlertTriangle, Zap, ChevronDown, ChevronRight } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';

const STATES = {
  LOADING_DRY: 'loading-dry',
  CONFIRM:     'confirm',
  EXECUTING:   'executing',
  RESULT:      'result',
};

export default function BulkApproveModal({ open, onClose, onCompleted, branchFilter }) {
  const token = useAuthStore((s) => s.token);
  const [state, setState] = useState(STATES.LOADING_DRY);
  const [error, setError] = useState(null);
  const [dryRun, setDryRun] = useState(null);
  const [result, setResult] = useState(null);
  const [excludedExpanded, setExcludedExpanded] = useState(false);
  const [skippedExpanded,  setSkippedExpanded]  = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setState(STATES.LOADING_DRY);
    setError(null);
    setDryRun(null);
    setResult(null);
    (async () => {
      try {
        const r = await api.ownerBulkApprovePreview(token, {
          branch_id: branchFilter ?? null,
        });
        if (!cancelled) {
          setDryRun(r);
          setState(STATES.CONFIRM);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || 'Failed to load eligibility preview');
          setState(STATES.CONFIRM);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, token, branchFilter]);

  const execute = async () => {
    if (!dryRun?.eligible_ids?.length) return;
    setState(STATES.EXECUTING);
    try {
      const r = await api.ownerBulkApproveExecute(token, dryRun.eligible_ids);
      setResult(r);
      setState(STATES.RESULT);
    } catch (e) {
      setError(e?.message || 'Bulk approve failed');
      setState(STATES.RESULT);
    }
  };

  const close = () => {
    if (result && onCompleted) onCompleted();
    onClose?.();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      data-testid="bulk-approve-modal"
      onClick={state !== STATES.EXECUTING ? close : undefined}
    >
      <div
        className="w-full max-w-xl rounded-3xl border border-white/10 bg-[#15151B] p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-gold mb-1">Bulk approval</div>
            <h2 className="text-xl font-bold text-white">
              {state === STATES.RESULT ? 'Result' : 'Approve Eligible Claims'}
            </h2>
          </div>
          <button
            type="button"
            onClick={close}
            disabled={state === STATES.EXECUTING}
            data-testid="bulk-approve-modal-close"
            className="p-1 rounded-lg text-white/50 hover:text-white hover:bg-white/5 disabled:opacity-40"
          >
            <X size={16} />
          </button>
        </div>

        {state === STATES.LOADING_DRY ? (
          <div className="py-10 flex flex-col items-center gap-3 text-white/60" data-testid="bulk-approve-modal-loading">
            <Loader2 size={24} className="animate-spin text-gold" />
            <div className="text-sm">Checking eligibility…</div>
          </div>
        ) : null}

        {state === STATES.CONFIRM && dryRun ? (
          <div data-testid="bulk-approve-modal-confirm">
            <div className="flex items-start gap-4 p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 mb-4">
              <Zap size={24} className="text-emerald-300 shrink-0 mt-1" />
              <div>
                <div className="text-3xl font-bold text-emerald-300" data-testid="bulk-approve-modal-eligible-count">
                  {dryRun.eligible_count}
                </div>
                <div className="text-sm text-emerald-200">eligible for auto-approval</div>
                <div className="text-xs text-white/50 mt-1">
                  Scanned {dryRun.total_candidates} pending claims.
                </div>
              </div>
            </div>

            {dryRun.excluded_count > 0 ? (
              <div className="mb-4">
                <button
                  type="button"
                  onClick={() => setExcludedExpanded((v) => !v)}
                  className="w-full flex items-center justify-between py-2 text-left text-sm text-amber-300 hover:text-amber-200"
                  data-testid="bulk-approve-modal-excluded-toggle"
                >
                  <span className="inline-flex items-center gap-2">
                    <AlertTriangle size={14} />
                    {dryRun.excluded_count} excluded
                  </span>
                  {excludedExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                {excludedExpanded ? (
                  <div className="mt-2 p-3 rounded-xl bg-white/[0.03] border border-white/10 text-xs text-white/70 space-y-1 max-h-48 overflow-auto"
                       data-testid="bulk-approve-modal-excluded-panel">
                    {Object.entries(dryRun.excluded_by_reason).map(([reason, count]) => (
                      <div key={reason} className="flex justify-between">
                        <span className="font-mono">{reason}</span>
                        <span className="text-white/50">{count}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={close}
                className="px-4 py-2 text-sm text-white/70 hover:text-white"
                data-testid="bulk-approve-modal-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={execute}
                disabled={dryRun.eligible_count === 0}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl bg-gold text-black hover:bg-gold-light disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="bulk-approve-modal-execute"
              >
                <Check size={14} /> Approve {dryRun.eligible_count} Claim{dryRun.eligible_count === 1 ? '' : 's'}
              </button>
            </div>
          </div>
        ) : null}

        {state === STATES.EXECUTING ? (
          <div className="py-10 flex flex-col items-center gap-3 text-white/60" data-testid="bulk-approve-modal-executing">
            <Loader2 size={24} className="animate-spin text-gold" />
            <div className="text-sm">Approving {dryRun?.eligible_count} claims…</div>
          </div>
        ) : null}

        {state === STATES.RESULT && result ? (
          <div data-testid="bulk-approve-modal-result">
            <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 mb-4">
              <div className="flex items-center gap-2">
                <Check size={20} className="text-emerald-300" />
                <div className="text-lg font-bold text-emerald-300" data-testid="bulk-approve-modal-approved-count">
                  Approved {result.approved_count} claim{result.approved_count === 1 ? '' : 's'}
                </div>
              </div>
              <div className="text-sm text-emerald-200 mt-1">
                {result.approved_count} stamp{result.approved_count === 1 ? '' : 's'} awarded.
              </div>
            </div>

            {result.skipped_count > 0 ? (
              <div className="mb-4">
                <button
                  type="button"
                  onClick={() => setSkippedExpanded((v) => !v)}
                  className="w-full flex items-center justify-between py-2 text-left text-sm text-amber-300 hover:text-amber-200"
                  data-testid="bulk-approve-modal-skipped-toggle"
                >
                  <span className="inline-flex items-center gap-2">
                    <AlertTriangle size={14} />
                    {result.skipped_count} skipped
                  </span>
                  {skippedExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                {skippedExpanded ? (
                  <div className="mt-2 p-3 rounded-xl bg-white/[0.03] border border-white/10 text-xs text-white/70 space-y-2 max-h-48 overflow-auto"
                       data-testid="bulk-approve-modal-skipped-panel">
                    {result.skipped.slice(0, 20).map((s) => (
                      <div key={s.claim_id} className="flex justify-between gap-3">
                        <span className="font-mono truncate">{s.claim_id.slice(0, 8)}</span>
                        <span className="text-white/50 text-right">{s.reasons.join(', ')}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="flex items-center justify-end pt-2">
              <button
                type="button"
                onClick={close}
                className="px-4 py-2 text-sm font-semibold rounded-xl bg-gold text-black hover:bg-gold-light"
                data-testid="bulk-approve-modal-done"
              >
                Done
              </button>
            </div>
          </div>
        ) : null}

        {error ? (
          <div className="mt-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-sm text-red-300">
            {error}
          </div>
        ) : null}
      </div>
    </div>
  );
}
